import { useRef, useState } from 'react'
import { Camera, Star, Trash2 } from 'lucide-react'
import { CERT_CLAIM } from '@sdg/shared'
import { Button } from '../components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog'
import { Field, Input } from '../components/ui/input'
import { Note } from '../components/ui/card'
import { cn } from '../lib/cn'
import { getSession } from '../lib/session'

/**
 * Collecting the Certificate of Participation.
 *
 * The Committee's change to slide 13: finishing all 14 booths makes the
 * certificate *available*, and the student collects it here. The form is the
 * one moment in the whole event when every attendee is motivated to reply, so
 * it asks for the two things worth having and nothing else.
 *
 *   - **A rating, 1–5.** Required, and the first thing on the form.
 *   - **A photo from the day**, one to five. Required.
 *   - **A comment** and **a link to a post**, both optional and both last.
 *
 * It is deliberately short. A student standing in a hall with a phone will
 * fill in two fields and abandon six, and an abandoned form means no
 * certificate — which would turn a reward into a punishment.
 */

/** Previews are object URLs, revoked when a photo is removed or sent. */
interface Picked {
  file: File
  url: string
}

export function ClaimCertificate({
  onClose,
  onClaimed,
}: {
  onClose: () => void
  /** Called once the certificate exists, so the pass can offer the download. */
  onClaimed: () => void
}) {
  const [rating, setRating] = useState(0)
  const [hover, setHover] = useState(0)
  const [comment, setComment] = useState('')
  const [social, setSocial] = useState('')
  const [photos, setPhotos] = useState<Picked[]>([])
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)

  const fileInput = useRef<HTMLInputElement>(null)

  const addPhotos = (list: FileList | null) => {
    if (!list) return
    setError(null)

    const room = CERT_CLAIM.maxPhotos - photos.length
    const incoming = Array.from(list).slice(0, room)

    if (Array.from(list).length > room) {
      setError(`You can send up to ${CERT_CLAIM.maxPhotos} photos.`)
    }

    const kept: Picked[] = []
    for (const file of incoming) {
      if (file.size > CERT_CLAIM.maxPhotoBytes) {
        setError(`“${file.name}” is too large — ${CERT_CLAIM.maxPhotoBytes / 1024 / 1024} MB max.`)
        continue
      }
      kept.push({ file, url: URL.createObjectURL(file) })
    }

    setPhotos((p) => [...p, ...kept])
    /* Cleared so picking the same file twice in a row still fires onChange. */
    if (fileInput.current) fileInput.current.value = ''
  }

  const removePhoto = (i: number) => {
    setPhotos((p) => {
      URL.revokeObjectURL(p[i].url)
      return p.filter((_, j) => j !== i)
    })
  }

  const ready = rating > 0 && photos.length >= CERT_CLAIM.minPhotos

  async function submit() {
    if (!ready || sending) return
    setSending(true)
    setError(null)

    try {
      const form = new FormData()
      form.set('rating', String(rating))
      if (comment.trim()) form.set('comment', comment.trim())
      if (social.trim()) form.set('socialPostUrl', social.trim())
      for (const p of photos) form.append('photos', p.file)

      /*
       * `fetch` directly rather than the shared `api` helper: this is
       * multipart, and that helper sets a JSON content-type. Letting the
       * browser set it is what puts the multipart boundary in the header.
       */
      const res = await fetch('/api/student/certificate/claim', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getSession() ?? ''}` },
        body: form,
      })

      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null
        setError(messageFor(body?.error))
        setSending(false)
        return
      }

      photos.forEach((p) => URL.revokeObjectURL(p.url))
      onClaimed()
    } catch {
      setError('Could not send that — check your connection and try again.')
      setSending(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && !sending && onClose()}>
      <DialogContent className="max-h-[92dvh] max-w-md overflow-y-auto rounded-3xl p-0">
        <div className="px-5 pb-5 pt-6">
          <DialogTitle className="text-base">Collect your certificate</DialogTitle>
          <DialogDescription className="mt-1">
            You visited all the booths — well done. Two quick things and it is yours.
          </DialogDescription>

          {/* ── 1. The rating ── */}
          <div className="mt-5">
            <p className="text-[12.5px] font-bold text-ink">How was the event?</p>
            <div
              className="mt-1.5 flex gap-1"
              role="radiogroup"
              aria-label="Rate the event out of five"
              onMouseLeave={() => setHover(0)}
            >
              {[1, 2, 3, 4, 5].map((n) => {
                const lit = n <= (hover || rating)
                return (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={rating === n}
                    aria-label={`${n} out of 5`}
                    disabled={sending}
                    onMouseEnter={() => setHover(n)}
                    onClick={() => {
                      setRating(n)
                      setError(null)
                    }}
                    className="rounded-lg p-1 transition-transform active:scale-90 disabled:opacity-60"
                  >
                    <Star
                      className={cn(
                        'size-9 transition-colors',
                        lit ? 'fill-amber text-amber' : 'fill-transparent text-slate-300',
                      )}
                      strokeWidth={1.8}
                    />
                  </button>
                )
              })}
            </div>
          </div>

          {/* ── 2. The photos ── */}
          <div className="mt-5">
            <p className="text-[12.5px] font-bold text-ink">
              A photo from today
              <span className="ml-1 font-normal text-muted">
                — at least one, up to {CERT_CLAIM.maxPhotos}
              </span>
            </p>

            {photos.length > 0 && (
              <div className="mt-2 grid grid-cols-3 gap-2">
                {photos.map((p, i) => (
                  <div key={p.url} className="relative aspect-square overflow-hidden rounded-xl">
                    <img src={p.url} alt="" className="h-full w-full object-cover" />
                    <button
                      type="button"
                      disabled={sending}
                      onClick={() => removePhoto(i)}
                      aria-label="Remove this photo"
                      className="absolute right-1 top-1 grid size-7 place-items-center rounded-full bg-black/55 text-white backdrop-blur-sm disabled:opacity-50"
                    >
                      <Trash2 className="size-3.5" strokeWidth={2.2} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {photos.length < CERT_CLAIM.maxPhotos && (
              <button
                type="button"
                disabled={sending}
                onClick={() => fileInput.current?.click()}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-line bg-surface px-3 py-4 text-sm font-semibold text-muted transition-colors hover:border-teal hover:text-teal disabled:opacity-60"
              >
                <Camera className="size-4" strokeWidth={2.2} />
                {photos.length === 0 ? 'Add a photo' : 'Add another'}
              </button>
            )}

            <input
              ref={fileInput}
              type="file"
              accept={CERT_CLAIM.photoTypes.join(',')}
              multiple
              hidden
              onChange={(e) => addPhotos(e.target.files)}
            />
          </div>

          {/* ── 3. The optional half ── */}
          <div className="mt-5 space-y-3">
            <Field label="Anything you'd like to tell us? (optional)" htmlFor="claim-comment">
              <Input
                id="claim-comment"
                value={comment}
                maxLength={CERT_CLAIM.commentMaxLength}
                placeholder="The best part of the day was…"
                disabled={sending}
                onChange={(e) => setComment(e.target.value)}
              />
            </Field>

            <Field label="Posted about it? Drop the link (optional)" htmlFor="claim-social">
              <Input
                id="claim-social"
                value={social}
                maxLength={CERT_CLAIM.socialUrlMaxLength}
                placeholder="https://"
                inputMode="url"
                autoCapitalize="none"
                spellCheck={false}
                disabled={sending}
                onChange={(e) => setSocial(e.target.value)}
              />
            </Field>
          </div>

          {error && (
            <Note tone="error" className="mt-3 text-xs">
              {error}
            </Note>
          )}

          <div className="mt-5 flex gap-2.5">
            <Button
              block
              variant="outline"
              disabled={sending}
              onClick={onClose}
              className="h-11 rounded-xl"
            >
              Later
            </Button>
            <Button
              block
              disabled={!ready || sending}
              onClick={submit}
              className="h-11 rounded-xl bg-[#1B2A6B] text-white hover:bg-[#141F55] disabled:opacity-50"
            >
              {sending ? 'Sending…' : 'Get my certificate'}
            </Button>
          </div>

          {!ready && (
            <p className="mt-2 text-center text-[11.5px] text-muted">
              {rating === 0 ? 'Give the event a rating' : 'Add at least one photo'} to continue.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** The server's refusals, in words a student can act on. */
function messageFor(code: string | undefined): string {
  switch (code) {
    case 'photo_required':
      return 'Please add at least one photo from the day.'
    case 'too_many_photos':
      return `You can send up to ${CERT_CLAIM.maxPhotos} photos.`
    case 'photo_too_large':
      return `One of those is too large — ${CERT_CLAIM.maxPhotoBytes / 1024 / 1024} MB max each.`
    case 'photo_wrong_type':
      return 'Photos only, please — JPG, PNG or HEIC.'
    case 'rating_required':
      return 'Please give the event a rating out of five.'
    case 'social_url_invalid':
      return 'That link does not look right. Leave it empty if you would rather not share one.'
    case 'already_claimed':
      return 'You have already collected this — reload the page to download it.'
    case 'not_eligible':
      return 'Visit all the booths first, then come back for your certificate.'
    default:
      return 'Could not send that — please try again.'
  }
}
